import {
    AppEntity,
    BetaTestingStyle,
    BuildDistroBucketBase,
    Environment,
    OrganizationEntity,
    Release,
    ReleaseStatus,
    ReleaseStepType,
    ReleaseType,
    SubscriptionStatus,
    User,
    UserPermissions,
} from '../types';
import { StringKeyedDictionary } from '../typescriptHelpers';
import * as amplitude from '@amplitude/analytics-browser';
import { extractSubdomainFromHostname } from '../helpers';
import LogRocket from 'logrocket';
import {
    GOOGLE_ADS_CONVERSION_ID,
    LINKED_IN_CONVERSION_ID,
    STACK_OVERFLOW_CONVERSION_PIXEL,
    BD_LOGROCKET_ORG_IDS,
} from '../constants';
import * as GoogleAnalytics from './ga';
import { reduce } from 'lodash';

// @ts-ignore
declare global {
    interface Window {
        dataLayer: Record<string, any>;
    }
}
export enum AmplitudeGroupType {
    Org = 'org',
}

export enum TrackingService {
    Amplitude = 'Amplitude',
    LogRocket = 'LogRocket',
}

export interface AmplitudeEvent {
    name: string;
    properties: StringKeyedDictionary<unknown>;
}

export interface UserProperties {
    permissions: string[];
    roles: string[];
    createdOn: string;
    isAdmin: boolean;
    isRunwayAdmin: boolean;
}

export interface OrgProperties {
    orgId: string;
    orgName: string;
    orgCreatedAt: string;
    orgApps: string[];
    orgAppsCount: number;
    orgAppGroups: string[];
    orgIntegrationProviderIds: string[];
    orgSubscriptionStatus: SubscriptionStatus;
    orgTrialDaysRemaining?: number;
    usesWorkflows: boolean;
}

export interface AppProperties {
    appId: string;
    appName: string;
    appPlatform: string;
    appIntegrationIds: string[];
    appIntegrationsCount: number;
    //appUsesStaticReleaseBranch: boolean;
    appBetaTestingStyle: BetaTestingStyle;
    appEnabledAutomationsCount: number;
    appFixRequestApprovalRequired: boolean;
}

export interface ReleaseProperties {
    releaseId: string;
    releaseVersion: string;
    // isReleasePilotFromRotation: boolean; // TODO get from BE
    isTargetKickoffDatePresent: boolean;
    isTargetReleaseDatePresent: boolean;
    releaseType: ReleaseType;
    isReleaseIsKickedOff: boolean;
    releaseStatus: ReleaseStatus;
}

export interface ContextProperties {
    context: Context;
    releaseStepType?: ReleaseStepType;
}

export interface DefaultProperties {
    subdomain?: string;
}

export enum Event {
    ViewSignIn = 'view sign in',
    ViewSignUp = 'view sign up',
    ViewKickoff = 'view kickoff',
    SignUp = 'sign up',
    AddApp = 'add app',
    ViewFeatureReadiness = 'view feature readiness',
    ViewReleaseCandidate = 'view release candidate',
    ViewBetaTesting = 'view beta testing',
    ViewRegressionTesting = 'view regression testing',
    ViewMetadata = 'view metadata',
    ViewScreenshots = 'view screenshots',
    ViewApprovals = 'view approvals',
    ViewSubmission = 'view submission',
    ViewAppReview = 'view app review',
    ViewTakeoff = 'view takeoff',
    ViewAppSettings = 'view app settings',
    ViewIntegrations = 'view integrations',
    ViewAppHome = 'view app home',
    ViewRelease = 'view release',
    ViewOrgSettings = 'view org settings',
    ClickRelease = 'click release',
    ClickAppStoreConsoleButton = 'click app store console button',
    ApplyFilters = 'apply filters',
    ApplySort = 'apply sort',
    ViewReleaseDemo = 'view release demo mode',
    ViewAppsDemoMode = 'view apps demo mode',
    ViewReleaseOverview = 'view release overview',
    ViewOrgOverview = 'view org overview',
    ClickOrgOverviewAppGroup = 'click org overview app group',
    ClickOrgOverviewAppGroupApp = 'click org overview app group app',
    ApplyNonBlockingSteps = 'apply non-blocking steps',
    AddWebhookEndpoint = 'add webhook endpoint',
    ClickDemoModePrimaryCta = 'click demo mode primary cta',
    ClickDemoModeSecondaryCta = 'click demo mode secondary cta',
    CompleteOnboardingSteps = 'complete qual onboarding tasks',
    ClickOnboardingTaskCta = 'click qual onboarding task cta',
    AddPublicApiKey = 'add public api key',
    DeletePublicApiKey = 'delete public api key',
    ClickViewFullEventTimeline = 'click view full event timeline cta',
    ViewRolloutMonitoring = 'view rollout monitoring',
    ClickGetNotified = 'click get notified',
    ViewIntegrationRefreshModule = 'view integration refresh module',
    ClickDismissOnboarding = 'click dismiss qual onboarding',
    ClickViewOnboarding = 'click view qual onboarding',
    ClickDismissQuickStartCI = 'click dismiss quick start ci',
    ClickLaunchTour = 'click launch tour',
    ViewFeatureFlags = 'view feature flags',
    ViewQuickStartCIStep = 'view quick start ci step',
    ClickStartQuickStartCI = 'click start quick start ci',
    ClickQuickStartCICta = 'click quick start ci cta',
    ClickShareInstallLinkToComms = 'click share install link to slack',
    BuildDistroViewOptInPage = 'view build distro opt in page',
    BuildDistroViewRecentBuilds = 'view build distro recent builds',
    BuildDistroViewBucket = 'view build distro bucket',
    BuildDistroViewBuildDetails = 'view build distro build details',
    BuildDistroViewSharedWithMe = 'view shared with me builds',
    BuildDistroClickBuildInstallCta = 'click build install cta',
    BuildDistroClickShareBuildCta = 'click share build cta',
    BuildDistroClickShareBucketCta = 'click share bucket cta',
    BuildDistroClickDownloadArtifactCTA = 'download build distro artifact',
    ClickAppHomeGetStartedOption = 'click app home get started option',
    ClickActivateOrgSubscriptionTrial = 'click activate org subscription trial',
    ViewProfilesAnDevices = 'view profiles and devices',
    ClickCopyFRViewLink = 'click copy fr view link',
    ClickPingPendingFROwners = 'click ping pending fr owners',
    ClickCopyStoreEmailForwardingAddressLink = 'click copy store email forwarding address link',
    ViewReleaseCalendar = 'view release calendar',
    ViewAppReleaseCalendar = 'view app release calendar',
    ViewReleaseTimeline = 'view release timeline',
    ViewStepTimeline = 'view step timeline',
    ApplyTimelineFilters = 'apply timeline filters',
    ApplyShowAutomationEventsOnly = 'apply show automation events only',
}

export enum QuickStartCICTAs {
    DownloadFiles = 'downloadFiles',
    ScanProject = 'scanProject',
    OpenPR = 'openPR',
    UploadRepoSecret = 'uploadRepoSecret',
}

export enum QuickStartCISource {
    AddApps = 'addApps',
    Onboarding = 'onboarding',
}

export enum Context {
    ReleasesTimeline = 'releases timeline',
    ReleaseStep = 'release step',
}

export const defaultProperties: (currentWindow?: Window) => DefaultProperties = (currentWindow = window) => {
    const subdomain = extractSubdomainFromHostname(currentWindow.location.hostname);
    const url = currentWindow.location.href;

    const properties = {
        subdomain,
        url,
    };

    return properties;
};

export const buildDistroBucketProperties: (bucket: BuildDistroBucketBase) => StringKeyedDictionary<unknown> = (
    bucket,
) => {
    return {
        bucketName: bucket.name,
        bucketType: bucket.type,
        bucketTestersCount: bucket.members.length,
        orgWideAccessEnabled: bucket.orgWideAccessEnabled,
        publicBucketLinkEnabled: bucket.publicBucketLinkEnabled,
    };
};

export const contextProperties: (context: Context, releaseStepType?: ReleaseStepType) => ContextProperties = (
    context,
    releaseStepType,
) => {
    return {
        context: context,
        releaseStepType,
    };
};

export const userProperties: (user: User) => UserProperties = (user) => {
    const permissions = user.permissions;
    const groups = user.groups;
    const isAdmin = permissions.includes(UserPermissions.Admin);
    const isRunwayAdmin = permissions.includes(UserPermissions.RunwayAdmin);

    return {
        userId: user.uuid,
        createdOn: user.createdAt,
        permissions,
        roles: groups,
        isAdmin,
        isRunwayAdmin,
        email: user.email,
        ...user.analytics,
    };
};

export const orgProperties: (
    org: OrganizationEntity,
    apps: string[],
    appGroups: string[],
    usesWorkflows: boolean,
) => OrgProperties = (org, apps, appGroups, usesWorkflows) => {
    const subscriptionInfo = org.subscriptionInfo;

    return {
        orgId: org.orgId,
        orgName: org.name,
        orgCreatedAt: org.createdAt,
        orgIntegrationProviderIds: org.integrationProviders.map((provider) => provider.id),
        orgApps: apps,
        orgAppsCount: apps.length,
        orgAppGroups: appGroups,
        orgSubscriptionStatus: subscriptionInfo.subscriptionStatus,
        orgTrialDaysRemaining: subscriptionInfo.trialDaysRemaining,
        usesWorkflows: usesWorkflows,
    };
};

export const appProperties: (app: AppEntity) => AppProperties = (app) => {
    const enabledAutomationsCount = reduce(
        app.appSettings.automations,
        (enabledCount, automation) => {
            if (automation.enabled) {
                return enabledCount + 1;
            }

            return enabledCount;
        },
        0,
    );

    return {
        appId: app.appId,
        appName: app.appSettings.appName,
        appPlatform: app.appSettings.platform,
        appUsesStaticReleaseBranch: app.appSettings.usesStaticReleaseBranchForNonHotfixes,
        appIntegrationIds: app.appSettings.integrations.map((integration) => integration.id),
        appIntegrationsCount: app.appSettings.integrations.length,
        appBetaTestingStyle: app.appSettings.betaTestingStyle,
        appEnabledAutomationsCount: enabledAutomationsCount,
        appBuildDistroEnabled: app.appSettings.buildDistributionEnabled,
        appFixRequestApprovalRequired: app.appSettings.requiresFixRequestsApprovals,
    };
};

export const releaseProperties: (release: Release) => ReleaseProperties = (release) => {
    return {
        releaseId: release.releaseId,
        releaseVersion: release.version,
        releaseType: release.type,
        releaseStatus: release.status,
        isReleaseIsKickedOff: release.isKickedOff,
        isTargetKickoffDatePresent: !!release.targetKickoffDate,
        isTargetReleaseDatePresent: !!release.targetReleaseDate,
        // isReleasePilotFromRotation: false, // TODO!! get from BE
    };
};

export const track: (
    eventName: Event | string,

    properties: StringKeyedDictionary<any>,
    trackingServices?: TrackingService[],
) => void = (eventName, properties, trackingServices = [TrackingService.Amplitude, TrackingService.LogRocket]) => {
    // if this is a build distro event, filter LogRocket tracking based on org
    const isBuildDistroEvent = eventName.toString().startsWith('BuildDistro');
    const orgId = properties.orgId;

    let finalTrackingServices = trackingServices;
    if (isBuildDistroEvent && orgId && !BD_LOGROCKET_ORG_IDS.has(orgId)) {
        finalTrackingServices = trackingServices.filter((service) => service !== TrackingService.LogRocket);
    }

    if (process.env.NODE_ENV !== Environment.Production) {
        console.log(`Tracking analytic event to: ${finalTrackingServices.join(',')} \n Event name: ${eventName}`);
        // uncomment to see properties while logging
        // console.log('Properties: ', JSON.stringify(properties, null, 2));
    }

    // remove undefined/null properties
    const filteredProperties = Object.keys(properties).reduce((acc, key) => {
        if (properties[key] !== undefined && properties[key] !== null) {
            acc[key] = properties[key];
        }
        return acc;
    }, {});

    if (finalTrackingServices.includes(TrackingService.Amplitude)) {
        amplitude.track(eventName, filteredProperties);
    }

    if (finalTrackingServices.includes(TrackingService.LogRocket)) {
        LogRocket.track(eventName, filteredProperties);
    }
};

export function identify(
    userId: string,
    properties: StringKeyedDictionary<amplitude.Types.ValidPropertyType | (string | number | boolean)> | UserProperties,
    trackingService: TrackingService,
    orgId?: string,
) {
    switch (trackingService) {
        case TrackingService.Amplitude: {
            amplitude.setUserId(userId);

            const identifyEvent = new amplitude.Identify();

            for (const [key, value] of Object.entries(properties)) {
                identifyEvent.set(key, value);
            }

            amplitude.identify(identifyEvent);

            if (orgId) {
                amplitude.setGroup(AmplitudeGroupType.Org, orgId);
            }
            break;
        }

        case TrackingService.LogRocket: {
            const logrocketProperties = properties as StringKeyedDictionary<string | number | boolean>;
            LogRocket.identify(userId, logrocketProperties);
            break;
        }
    }
}

export const getAmplitudeSessionId: () => number = () => {
    return amplitude.getSessionId();
};

export const getAmplitudeDeviceId: () => string = () => {
    return amplitude.getDeviceId();
};

export const trackSignupConversionPixels: (
    source?: GoogleAnalytics.SignUpLoginMethod,
    userProperties?: GoogleAnalytics.GAUserProperties,
) => void = (source, userProperties) => {
    if (process.env.NODE_ENV !== Environment.Production) {
        console.log(`Skipping tracking conversion pixels on dev environment`);
        return;
    }

    // HubSpot

    (window as any).lintrk('track', { conversion_id: LINKED_IN_CONVERSION_ID });

    // Stack overflow
    const a = String(Math.floor(Math.random() * 10000000000000000));

    new Image().src = STACK_OVERFLOW_CONVERSION_PIXEL + `ord=${a}?`;

    // Reddit

    (window as any).rdt('track', 'SignUp');

    // GTM

    // add back google analytics track event

    const extraParams = source ? { method: source } : null;

    const params =
        extraParams ?
            {
                ...extraParams,
            }
        :   {};

    if (userProperties) {
        params['email'] = userProperties.userId;
        params['org_id'] = userProperties.orgId;
    }

    GoogleAnalytics.track(GoogleAnalytics.EventName.SignUp, extraParams, userProperties);

    window.dataLayer.push({
        event: GoogleAnalytics.EventName.SignUp,
        ...params,
    });

    // Google ads conversion

    GoogleAnalytics.track(GoogleAnalytics.EventName.Conversion, {
        send_to: GOOGLE_ADS_CONVERSION_ID,
    });

    // window.dataLayer.push({
    //     event: GoogleAnalytics.EventName.Conversion,
    //     send_to: GOOGLE_ADS_CONVERSION_ID,
    // });
};
